import { useEffect } from "react"
import { Head, router } from "@inertiajs/react"

type Row = Record<string, unknown>

interface Publication {
  slug: string
  race_name: string
  race_format: string | null
  is_official: boolean
  published_at: string
  general: Row[]
  empty: boolean
}

interface LiveProps {
  publication: Publication
  meta: { title: string }
  i18n: {
    official: string
    provisional: string
    empty_state: string
    rank: string
    rider: string
    club: string
    result: string
    updated_at: string
  }
}

const str = (value: unknown): string => (value == null ? "" : String(value))

const riderName = (row: Row): string => {
  const full = [row.first_name, row.last_name].filter(Boolean).map(str).join(" ").trim()
  return str(row.rider_name) || full || str(row.name) || "—"
}

const rank = (row: Row, index: number): string => str(row.rank) || str(row.position) || String(index + 1)

const result = (row: Row): string => str(row.time) || str(row.gap) || str(row.result) || ""

export default function Live({ publication, meta, i18n }: LiveProps) {
  // Poll for fresh results: reload only the `publication` prop every 10s.
  useEffect(() => {
    const id = setInterval(() => {
      router.reload({ only: ["publication"] })
    }, 10_000)

    return () => clearInterval(id)
  }, [])

  const updatedAt = new Date(publication.published_at).toLocaleTimeString("fr-FR")

  return (
    <>
      <Head>
        <title>{meta.title}</title>
      </Head>

      <div className="bg-[#0a0a0f] min-h-screen text-slate-300">
        <header className="max-w-4xl mx-auto px-6 pt-16 pb-8">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h1 className="text-3xl md:text-4xl font-bold text-white">{publication.race_name}</h1>
            <span
              className={
                publication.is_official
                  ? "px-3 py-1 text-xs font-semibold rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  : "px-3 py-1 text-xs font-semibold rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
              }
            >
              {publication.is_official ? i18n.official : i18n.provisional}
            </span>
          </div>
          <p className="text-sm text-slate-500">
            {i18n.updated_at} {updatedAt}
          </p>
        </header>

        <main className="max-w-4xl mx-auto px-6 pb-24">
          {publication.empty ? (
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-10 text-center">
              <p className="text-slate-400">{i18n.empty_state}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/[0.07]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.03] text-left text-slate-400">
                    <th className="px-4 py-3 font-medium w-16">{i18n.rank}</th>
                    <th className="px-4 py-3 font-medium">{i18n.rider}</th>
                    <th className="px-4 py-3 font-medium">{i18n.club}</th>
                    <th className="px-4 py-3 font-medium text-right">{i18n.result}</th>
                  </tr>
                </thead>
                <tbody>
                  {publication.general.map((row, index) => (
                    <tr key={index} className="border-t border-white/[0.05]">
                      <td className="px-4 py-3 font-semibold text-white">{rank(row, index)}</td>
                      <td className="px-4 py-3">{riderName(row)}</td>
                      <td className="px-4 py-3 text-slate-400">
                        {str(row.club_name) || str(row.club)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-300">{result(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </>
  )
}
